import { useMemo } from 'react';
import { useMyLeaveBalances } from './useLeaveBalances';
import { getLeaveTypeLabel } from '@/constants/leaveTypes';

export interface AssignedLeaveType {
  value: string;
  label: string;
  policy_id: string;
}

/**
 * Hook to get leave types that are actively assigned to the current user.
 * Returns deduplicated leave types based on active policy assignments.
 * 
 * Per Leave_type_filter.md:
 * - Q1: Same leave_type, multiple policies → ONE ENTRY (deduplicate by leave_type)
 * - Q4: Auto-populate policy_id → Use first active assignment for each leave_type
 */
export const useMyAssignedLeaveTypes = () => {
  const { data: balances, isLoading, error } = useMyLeaveBalances();

  const assignedLeaveTypes = useMemo(() => {
    if (!balances || balances.length === 0) {
      return [];
    }

    // Create a map to deduplicate by leave_type, keeping the first policy found
    const leaveTypeMap = new Map<string, AssignedLeaveType>();

    balances.forEach((balance) => {
      const leaveType = balance.policy?.leave_type;
      if (leaveType && !leaveTypeMap.has(leaveType)) {
        leaveTypeMap.set(leaveType, {
          value: leaveType,
          label: getLeaveTypeLabel(leaveType),
          policy_id: balance.policy_id,
        });
      }
    });

    // Convert to array and sort by label
    return Array.from(leaveTypeMap.values()).sort((a, b) => 
      a.label.localeCompare(b.label)
    );
  }, [balances]);

  // Policy ID lookup function for form submission
  const getPolicyIdForLeaveType = (leaveType: string): string | undefined => {
    return assignedLeaveTypes.find(lt => lt.value === leaveType)?.policy_id;
  };

  return {
    assignedLeaveTypes,
    isLoading,
    error,
    hasAssignments: assignedLeaveTypes.length > 0,
    getPolicyIdForLeaveType,
  };
};

